import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Container,
  LinearProgress,
  Typography,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import bkstApi from "../../api";
import { UserContext } from "../../context/UserContext";
import { hasManagerAccess, hasOwnerAccess } from "../../common/util";
import ClickToCopy from "../../common/ClickToCopy";
import AddStaff from "../components/AddStaff";
import EditStaff from "../components/EditStaff";

const ROLE_ORDER = ["owner", "manager", "clerk", "kitchen", "kitchen leader"];

const ROLE_DESCRIPTIONS = {
  all: "All active staff accounts for your bakery.",
  owner: "Full access to quotes, orders, catalog, settings, reports, and plan.",
  manager: "Day-to-day ops for quotes, orders, and catalog — without reports or plan.",
  clerk: "Point-of-sale access to create and manage orders.",
  kitchen: "Kitchen display for production — view and progress orders.",
  "kitchen leader":
    "Kitchen display plus order edits, payments, and refunds on the floor.",
};

function formatRoleLabel(role) {
  return role
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function canEditStaff(viewer, member) {
  if (!viewer?.role || !member) {
    return false;
  }
  if (hasOwnerAccess(viewer.role)) {
    // Owners can edit everyone except other owners
    if (member.role === "owner" || member.role === "baker") {
      return member.uid === viewer.uid;
    }
    return true;
  }
  if (hasManagerAccess(viewer.role)) {
    return ["clerk", "kitchen", "kitchen leader"].includes(member.role);
  }
  return false;
}

export default function MyStaff(props) {
  const { user } = useContext(UserContext);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");

  const storeLocations = user?.config?.storeLocation || [];
  const showLocations =
    user?.capabilities?.includes("multi-location") && storeLocations.length > 1;
  const locationLabelById = useMemo(() => {
    return storeLocations.reduce((map, loc) => {
      if (loc.id) {
        map[loc.id] = loc.address?.street || loc.id;
      }
      return map;
    }, {});
  }, [storeLocations]);

  const formatLocations = (member) => {
    const ids = Array.isArray(member.storeLocationIds) ? member.storeLocationIds : [];
    if (!ids.length) {
      return "All";
    }
    return ids.map((id) => locationLabelById[id] || id).join(", ");
  };

  const canViewAccessCode = (member) => {
    if (!member.accessCode) {
      return false;
    }
    if (hasOwnerAccess(user?.role)) {
      return true;
    }
    return (
      hasManagerAccess(user?.role) &&
      ["clerk", "kitchen", "kitchen leader"].includes(member.role)
    );
  };

  const sortStaffByName = (o1, o2) => {
    const name1 = `${o1.firstName || ""} ${o1.lastName || ""}`.trim();
    const name2 = `${o2.firstName || ""} ${o2.lastName || ""}`.trim();
    return name1.localeCompare(name2);
  };

  const loadStaff = useCallback(() => {
    setLoading(true);
    bkstApi(`/my/staff`).then((res) => {
      setStaff(res.data.staff.sort(sortStaffByName));
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const roleCounts = useMemo(() => {
    return staff.reduce((counts, member) => {
      const role = member.role || "unknown";
      counts[role] = (counts[role] || 0) + 1;
      return counts;
    }, {});
  }, [staff]);

  const roleTabs = ROLE_ORDER.filter((role) => roleCounts[role] > 0);
  const otherRoles = Object.keys(roleCounts)
    .filter((role) => !ROLE_ORDER.includes(role))
    .sort((a, b) => a.localeCompare(b));

  const filteredStaff = tab === "all" ? staff : staff.filter((member) => member.role === tab);

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 220,
      valueGetter: (params) => `${params.row.firstName || ""} ${params.row.lastName || ""}`.trim(),
      renderCell: (params) => {
        const name = `${params.row.firstName || ""} ${params.row.lastName || ""}`.trim();
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 0.5 }}>
            <Avatar
              src={`https://storage.googleapis.com/bakestreet-f55a0.appspot.com/img/u/${params.row.uid}.webp`}
              sx={{ width: 36, height: 36 }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
                {name}
              </Typography>
              {tab === "all" && (
                <Typography variant="caption" color="textSecondary">
                  {formatRoleLabel(params.row.role || "")}
                </Typography>
              )}
            </Box>
          </Box>
        );
      },
    },
    {
      field: "accessCode",
      headerName: "Access Code",
      width: 150,
      align: "right",
      headerAlign: "right",
      sortable: false,
      renderCell: (params) =>
        canViewAccessCode(params.row) ? (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.5 }}>
            <Typography
              variant="body2"
              sx={{
                fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                letterSpacing: "0.12em",
                fontWeight: 600,
              }}
            >
              {params.row.accessCode}
            </Typography>
            <ClickToCopy text={params.row.accessCode} />
          </Box>
        ) : (
          <Typography variant="body2" color="textSecondary">
            —
          </Typography>
        ),
    },
    ...(showLocations
      ? [
          {
            field: "storeLocationIds",
            headerName: "Locations",
            flex: 1,
            minWidth: 160,
            sortable: false,
            valueGetter: (params) => formatLocations(params.row),
            renderCell: (params) => (
              <Typography variant="body2" color="textSecondary" noWrap title={formatLocations(params.row)}>
                {formatLocations(params.row)}
              </Typography>
            ),
          },
        ]
      : []),
    {
      field: "actions",
      headerName: "",
      width: 56,
      sortable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) =>
        canEditStaff(user, params.row) ? (
          <EditStaff staff={params.row} onSaved={loadStaff} />
        ) : null,
    },
  ];

  const rows = filteredStaff.map((member, index) => ({
    id: member.uid || index,
    ...member,
  }));

  if (loading) {
    return <LinearProgress />;
  } else {
    return (
      <Container maxWidth={showLocations ? "lg" : "md"}>
        <Box>
          <Box
            mt={4}
            mb={2}
            sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}
          >
            <Box>
              <Typography variant="h1" gutterBottom>
                Staff
              </Typography>
              <Typography variant="body1" color="textSecondary">
                View staff accounts and access codes, or add a new team member.
              </Typography>
            </Box>
            <AddStaff onCreated={loadStaff} />
          </Box>
          <Box mb={1}>
            <Tabs
              value={tab}
              onChange={(_, value) => setTab(value)}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ borderBottom: "1px solid #ECECEC" }}
            >
              <Tab value="all" label={`All (${staff.length})`} />
              {[...roleTabs, ...otherRoles].map((role) => (
                <Tab
                  key={role}
                  value={role}
                  label={`${formatRoleLabel(role)} (${roleCounts[role]})`}
                />
              ))}
            </Tabs>
          </Box>
          {ROLE_DESCRIPTIONS[tab] && (
            <Box my={2}>
              <Alert severity="info">{ROLE_DESCRIPTIONS[tab]}</Alert>
            </Box>
          )}
          <Box
            sx={{
              width: "100%",
              display: "grid",
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#FAFAFA",
              },
            }}
          >
            <DataGrid
              rows={rows}
              columns={columns}
              autoHeight
              disableRowSelectionOnClick
              disableColumnMenu
              hideFooter
              getRowHeight={() => 56}
              initialState={{
                sorting: {
                  sortModel: [{ field: "name", sort: "asc" }],
                },
              }}
            />
          </Box>
        </Box>
      </Container>
    );
  }
}
